import {
  AsyncPipe,
  CommonModule,
  NgIf
} from '@angular/common';
import { HttpClient, HttpEventType } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  DestroyRef,
  DOCUMENT,
  effect,
  EventEmitter,
  Inject,
  Injector,
  input,
  OnDestroy,
  OnInit,
  Output,
  Signal,
  signal,
  TemplateRef
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  AsyncValidatorFn,
  FormControl,
  FormGroup,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validators
} from '@angular/forms';
import {
  ButtonDirective,
  FormCheckComponent,
  FormCheckInputDirective,
  FormCheckLabelDirective,
  FormControlDirective,
  FormDirective,
  FormFeedbackComponent,
  FormFloatingDirective,
  FormLabelDirective,
  InputGroupComponent,
  InputGroupTextDirective
} from '@coreui/angular-pro';
import { IconModule } from '@coreui/icons-angular';
import { OwlDateTimeModule, OwlNativeDateTimeModule } from '@danielmoncada/angular-datetime-picker';
import { ServerAuthUrlPipe } from 'auth';
import { ConferenceService } from 'conference';
import { TranslateDirective, TranslatePipe, TranslateService } from 'i18n';
import { ImageLink, Media } from 'media';
import moment from 'moment-timezone';
import { NgxColorsModule, validColorValidator } from "ngx-colors";
import { asyncScheduler, catchError, combineLatest, debounceTime, distinctUntilChanged, filter, map, Observable, of, pairwise, partition, startWith, Subject, Subscription, switchMap, tap, timer } from 'rxjs';
import { clipboardCopy, contrastColor, ENVIRONMENT, FileSizePipe, FormChangeDetector, Logger, Platform, SafeUrlPipe, ToastService } from 'shared';
import { CalendarEvent, NULL_CALENDAR_EVENT } from '../../models/event';

type FormValue = Partial<CalendarEvent> | null;

type FormState = {
  valid: boolean;
  dirty: boolean;
}
type JoinLinkOrRoomNameState = 'link' | 'room' | 'pending';

type EventForm = {
  name: FormControl<string>;
  description: FormControl<string>;
  timeFrom: FormControl<Date>;
  timeTo: FormControl<Date | null>;
  location: FormControl<string>;
  joinLinkOrRoomName: FormControl<string>;
  color: FormControl<string>;
  media: FormControl<Media | null>;
  language: FormControl<string>;
  lobbyBypass: FormControl<boolean>;
};

export function roomNameValidator(
  conferenceService: ConferenceService,
  joinLinkOrRoomNameState$: Signal<JoinLinkOrRoomNameState>,
  eventId$: Signal<string | undefined>
): AsyncValidatorFn {

  return (control: AbstractControl): Observable<ValidationErrors | null> => {
    const value = (control.value || '').toLowerCase().trim();

    if (joinLinkOrRoomNameState$() !== 'room') {
      return of(null);
    }

    // wait for user to stop typing
    return timer(400).pipe(
      switchMap(() =>
        conferenceService.validateRoomName(control.value, eventId$())
      ),
      map(isValid =>
        isValid ? null : { exists: true }
      ),
      catchError(() => of(null)) // don't block form on server error
    );
  };
}

@Component({
    selector: 'event-detail',
    standalone: true,
    templateUrl: './event-detail.component.html',
    styleUrl: './event-detail.component.scss',
    imports: [
      ReactiveFormsModule,
      CommonModule,
      ButtonDirective,
      FormControlDirective,
      FormLabelDirective,
      FormDirective,
      FormFloatingDirective,
      FormFeedbackComponent,
      FormCheckComponent,
      FormCheckInputDirective,
      FormCheckLabelDirective,
      InputGroupComponent,
      InputGroupTextDirective,
      OwlDateTimeModule,
      OwlNativeDateTimeModule,
      IconModule,
      NgIf,
      AsyncPipe,
      FileSizePipe,
      ServerAuthUrlPipe,
      SafeUrlPipe,
      TranslateDirective,
      TranslatePipe,
      NgxColorsModule
    ],
    exportAs: 'eventDetail',
    changeDetection: ChangeDetectionStrategy.OnPush
  })
  export class EventDetailComponent implements OnInit, OnDestroy {
    event$ = input<CalendarEvent | null | undefined>();
    editMode$ = input<boolean>();

    controlsTemplate = contentChild<TemplateRef<any>>(TemplateRef);
    
    @Output() save = new EventEmitter<FormValue>();
    @Output() delete = new EventEmitter<string>();
    @Output() cancel = new EventEmitter<void>();
    @Output() formState = new EventEmitter<FormState>();

    value$ = signal<FormValue>(null);
    eventId$ = computed(() => this.event$()?.id);

    form = signal<FormGroup<EventForm> | null>(null);
    formState$ = signal<FormState>({ valid: true, dirty: false });
    protected formChangeSubscription: Subscription | null = null;
    protected formChangeReset: Subject<void> | null = null;
  
    file: File | null = null;
    uploading = signal(false);
    uploadProgress$ = signal<number>(0);
    uploadError = false;
    isDragging = false;

    timeFromMin: Date | null = null;
    timeFromMax: Date | null = null;
    timeToMin  : Date | null = null;
    timeToMax  : Date | null = null;
    
    private readonly localTimezone = moment.tz.guess();

    readonly defaultColor  = '#017bd9';
    readonly contrastColor$ = computed(() => {
      const color = this.value$()?.color || this.defaultColor;
      return contrastColor(color);
    });

    private readonly joinLinkRegex = /^https?:\/\//i;
    joinLinkOrRoomName$ = signal('');
    joinLinkOrRoomNameState$ = computed<JoinLinkOrRoomNameState>(() => {
      const value = this.joinLinkOrRoomName$().trim().toLowerCase();
      if (!value) {
        return 'pending';
      } else {
        if (this.joinLinkRegex.test(value)) {
          return 'link';
        } else if ('http://'.startsWith(value) || 'https://'.startsWith(value)) {
          return 'pending';
        }
        return 'room';
      }
    });

    joinLink$ = computed<string | undefined>(() => {
      let joinLink = this.event$()?.joinLink;
      if (joinLink) {
        joinLink = `${joinLink.startsWith('/') ? `${window.location.origin}` : ''}${joinLink}`;
      }
      return joinLink;
      
      // const value = this.joinLinkOrRoomName$().trim();
      // const state = this.joinLinkOrRoomNameState$();
      // if (state === 'link') return value;
      // if (state === 'room') return `${window.location.origin}/conference/${encodeURIComponent(value)}`; // `https://${this.environment.conferenceDomain}/${encodeURIComponent(value)}`
      // return undefined; // pending
    });

    joinLinkOrRoomNameLabel$ = computed(() => {
      const key = ({
        link: 'joinLink',
        room: 'roomName',
        pending: 'joinLinkOrRoomName',
      } as const)[this.joinLinkOrRoomNameState$()];
      return this.translateService.translateAsync(`calendar.event.${key}`);
    });
    
    CalendarEvent = CalendarEvent;
    protected logger = new Logger('EventDetailComponent');
  
    constructor(
      private fb: NonNullableFormBuilder,
      private http: HttpClient,
      private conferenceService: ConferenceService,
      protected translateService: TranslateService,
      private formChangeDetector: FormChangeDetector,
      private toastService: ToastService,
      private destroyRef: DestroyRef,
      private injector: Injector,
      public platform: Platform,
      @Inject(NULL_CALENDAR_EVENT) private nullCalendarEvent: CalendarEvent,
      @Inject(ENVIRONMENT) private environment: any,
      @Inject(DOCUMENT) private document: Document
    ) {
      effect(() => {
        const state = this.formState$();
        this.formState.next(state);
      })
    }
  
    ngOnInit(): void {
      const toObservableOptions = { injector: this.injector };
      const inputChange$ = combineLatest([
        toObservable(this.event$, toObservableOptions).pipe(
          filter(event => {
            return event===null || event!==undefined
          }),
          map(event => {
            return { ...this.nullCalendarEvent, ...(event || {})}
          }
        )),
        toObservable(this.editMode$, toObservableOptions).pipe(
          filter(editMode => editMode!=undefined),
          distinctUntilChanged((m1, m2) => m1==m2)
        )
      ]).pipe(
        debounceTime(100),
        switchMap(async ([event, editMode], index) => {
          const round = (date: number | moment.Moment, duration: number | moment.Duration) =>
            moment(Math.ceil(+date / +duration) * +duration).startOf('minutes');
          const toLocalDate = (time?: number | Date | moment.Moment | null) : Date =>
            moment(time).tz(this.localTimezone).toDate();
          const timeFrom = /*event.timeCreated &&*/ event.id && event.timeFrom // TODO: db conferences do not have timeCreated - fix on server
            ? moment(event.timeFrom)
            : round(moment().add(10, 'minutes'), moment.duration(10, 'minutes'));
          const timeTo = event.id && event.timeTo ? moment(event.timeTo) : moment(timeFrom).add(1, 'hours');
          const isConference = CalendarEvent.isConference(event);
          const joinLinkOrRoomName = isConference ? event.properties?.room_name : event.joinLink;
          const value: any = {
            name: event.name,
            author: event.author?.full_name,
            description: event.description,
            type: event.type,
            location: event.location,
            joinLinkOrRoomName: joinLinkOrRoomName,
            color: event.color,
            timeCreated: toLocalDate(event.timeCreated),
            timeFrom: toLocalDate(timeFrom),
            timeTo: toLocalDate(timeTo),
            timezone: event.timezoneId,
            language: event.language,
            lobbyBypass: isConference ? event.properties?.lobby_bypass ?? true : undefined
          };
          const media = event.media ? event.media : await this.defaultMedia;
          value.media = media;
          return [event, editMode, value, index] as [typeof event, boolean, typeof value, number];
        })
      );

      const [edit$, view$] = partition(
        inputChange$,
        ([event, editMode, value, index]) => editMode
      );

      edit$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(([event, editMode, value, index]) => {
          let form = this.form();
          if (!form) {
            this.form.set(form = this.createEventForm());
          }
          const triggerChangeDetection = (value: FormValue) => {
            // feeds form change detector with changes
            this.value$.set(value);
          };
          let formChangeSubscription = this.formChangeSubscription;
          if (!formChangeSubscription) {
            this.formChangeSubscription = formChangeSubscription = new Subscription();
            const detectorOptions = {
              trigger$: toObservable(this.value$, toObservableOptions).pipe(tap(value => this.logger.debug('trigger$', {value}))),
              normalizer: function(value: any) {
                // media selection will assign the complete media object to the corresponding form control value although
                // the event is delivered with its media property containing just some essential media attributes
                // to avoid false detection of media change just the media id is compared.
                const mediaId = value?.media?.id;
                // value.media = mediaId ? { id: mediaId } : value.media;
                // return Promise.resolve(value);
                // do not modify the value - clone it and keep the data in this.value$() intact
                return Promise.resolve({...value, media: mediaId ? { id: mediaId } : value.media});
              }.bind(this),
              log: true
            };

            formChangeSubscription.add(form.statusChanges
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe(() => {
              this.formState$.update((state) => ({...state, valid: form.valid}));
            }));

            formChangeSubscription.add(form.valueChanges
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe(state => {
                  this.logger.debug('onValueChange', {state});
                  triggerChangeDetection(this.value);
                })
            );

            formChangeSubscription.add(form.get('joinLinkOrRoomName')!.valueChanges
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe(value => {
                this.joinLinkOrRoomName$.set(value ?? '');
                // form.get('joinLinkOrRoomName')!.updateValueAndValidity({ onlySelf: true, emitEvent: true });
              })
            );
    

            formChangeSubscription.add(form.get('timeFrom')?.valueChanges
              .pipe(
                takeUntilDestroyed(this.destroyRef),
                startWith(null),
                pairwise()
              )
              .subscribe(([prevTimeFrom, currTimeFrom]) => {
                const timeToControl = form.get('timeTo')!;
                let timeTo: Date | null = timeToControl.value;
                if (currTimeFrom) {
                  const delta = timeTo && prevTimeFrom ? moment(timeTo).diff(prevTimeFrom) : moment.duration(1, 'hours');
                  timeTo = moment(currTimeFrom).add(delta).toDate();
                  this.timeToMin = new Date(currTimeFrom);
                } else {
                  timeTo = null;
                }
                if (timeTo != timeToControl.value) {
                  timeToControl.setValue(timeTo, {onlySelf: true, emitEvent: false});
                }
              })
            );

            this.formChangeReset = new Subject();
            const detector = this.formChangeDetector.detect(form, {...detectorOptions, resetter: this.formChangeReset});

            formChangeSubscription.add(
              detector.subscribe((pristine: boolean) => {
                this.logger.debug('PRISTINE', pristine);
                this.formState$.update((state) => ({ ...state, dirty: !pristine }));
              })
            );

            formChangeSubscription.add(() => {
              this.formChangeSubscription = null;
              this.formChangeReset?.complete();
              this.formChangeReset = null;
              this.form.set(null);
              this.logger.debug('formChangeSubscription -> unsubscribed!');
            });
            form.reset(value);
          } else { // formChangeSubscription initialization
            this.logger.debug('RESET change detector', { value });
            form.reset(value, { onlySelf: true, emitEvent: true });
            // form.reset() effect is async (i.e. the control value is changed with a delay).
            // leverage js loop task to reset the detector state while ensuring the new form value is set 
            // and propagated to the change detector trigger$ observable.
            // We could also do: 
            // trigger$
            // .pipe(skip(1),take(1), takeUntilDestroyed(this.destroyRef))
            // .subscribe(() => this.formChangeReset?.next();)
            asyncScheduler.schedule(() => { // queueMicrotask(() => {
              this.formChangeReset?.next();
            }, 0);
          }
          this.timeFromMin = this.timeToMin = value.timeCreated;
      });

      view$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(([event, editMode, value, index]) => {
          if (this.formChangeSubscription) {
            this.formChangeSubscription.unsubscribe();
          }
          this.value$.set(value);
        });
    }
  
    ngOnDestroy(): void {
      this.formChangeSubscription?.unsubscribe();
    }
  
    private createEventForm() {
      const now = moment.now();
      const timeCreated = moment(now);
      const timeFrom = moment(timeCreated).add(1, 'hours').startOf('hour').toDate();
      return this.fb.group({
        name: ['', [
          Validators.required,
          Validators.minLength(3),
        ]],
        description: [''],
        timeFrom : [ timeFrom , Validators.required ],
        timeTo   : this.fb.control<Date | null>(null, [Validators.required]),
        location: [''],
        joinLinkOrRoomName: ['',  {
          validators: [Validators.required],
          asyncValidators: [roomNameValidator(this.conferenceService, this.joinLinkOrRoomNameState$, this.eventId$)],
          updateOn: 'change' // default, explicit for clarity
        }],
        color: [this.defaultColor, [Validators.required, validColorValidator]],
        media: this.fb.control<Media | null>(null),
        language: [''],
        lobbyBypass: [ false ]
      });
    }

    private get value(): FormValue {
      const {
        name,
        description,
        timeFrom,
        timeTo,
        location,
        joinLinkOrRoomName,
        color,
        media,
        language,
        lobbyBypass
      } = this.form()?.getRawValue() || {};
    
      const toUtcMillis = (date?: Date | null) =>
        date
          ? moment.tz(date, this.localTimezone).utc().valueOf()
          : undefined;
    
      const eventId = this.eventId$();
      const event: FormValue = {
        id: eventId,
        name: name?.trim(),
        description: description?.trim() || undefined,
        timeFrom: toUtcMillis(timeFrom),
        timeTo: toUtcMillis(timeTo),
        timezoneId: this.localTimezone,
        location: location?.trim() || undefined,
        // joinLink: this.joinLink$()?.trim() || undefined,
        color,
        ...(media && { media }), // include media only if it exists
        language,
        properties: {
          lobbyBypass
        }
      };
      const linkOrRoom = joinLinkOrRoomName?.trim() || undefined;
      if (linkOrRoom) {
        const state = this.joinLinkOrRoomNameState$();
        if (state=='room') {
          event.properties!['room_name'] = linkOrRoom;
        } else if (state=='link') {
          event.joinLink = linkOrRoom;
        }
      }
      return event;
    }

    // private setCoverSrc(media: Media | null) {
    //   this.value$.update(value => ({...value, media: media ?? undefined}));
    // }

    onTimeFromChange(event: any ) {
      this.timeToMin  = event.value;
    }

    onMediaRemove(event: MouseEvent) {
      event.stopPropagation();
      this.file = null;
      this.defaultMedia.then(media => {
        this.form()?.get('media')?.setValue(media);
      })
    }
  
    onSave() {
      const state = this.formState$();
      if (state.valid && state.dirty) {
        const event = this.value$();
        if (event?.media?.id=='0') {
          delete event?.media;
        }
        if (event?.id) {
          this.save.emit(event);
        }
      }
    }

    onDelete() {
      if (this.eventId$()) {
        this.delete.emit(this.eventId$());
      }
    }

    onCancel() {
      this.cancel.emit();
    }

    onColorPickerInput(color: string) {
      this.form()?.get('color')?.setValue(color);
    }
  
    onDragOver(event: DragEvent) {
      event.preventDefault();
      event.stopPropagation();
      this.isDragging = true;
    }
  
    onDrop(event: DragEvent) {
      event.preventDefault();
      event.stopPropagation();
      this.isDragging = false;
      const file = event.dataTransfer?.files?.[0];
      this.handleFile(file);
    }
  
    onFileSelected(event: Event) {
      const input = event.target as HTMLInputElement;
      this.handleFile(input.files?.[0]);
    }
  
    handleFile(file: File | undefined) {
      if (file) {
        this.file = file;
        this.uploadError = false;
        const reader = new FileReader();
        reader.onload = () => this.upload(file, { name: this.form()?.get('name')?.value, tags: ['upload', 'event.cover'] });
        reader.readAsDataURL(file);
      }
    }
  
    upload(file: File, options?: {name?: string, tags: string[]}) {
      this.uploading.set(true);
      this.uploadProgress$.set(0);
  
      const formData = new FormData();
      formData.append('file', file);

      const name = options?.name?.trim();
      if (name) {
        formData.append('name', name);
      }

      options?.tags?.forEach(tag => {
        formData.append('tags[]', tag);
      })
  
      this.http
        .post<Media>('/media/upload', formData, {
          reportProgress: true,
          observe: 'events',
        })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (event) => {
            if (event.type === HttpEventType.UploadProgress) {
              this.uploadProgress$.set(Math.round(100 * event.loaded / (event.total || 1)));
            } else if (event.type === HttpEventType.Response) {
              const media = event.body;
              if (media) {
                this.logger.debug('Media upload completed', media);
                this.form()?.patchValue({ media });
              }
              setTimeout(() => {
                this.uploading.set(false);
                this.uploadProgress$.set(0);
              }, 1000);
            }
          },
          error: async (error) => {
            this.uploading.set(false);
            this.uploadError = true;
            this.form()?.patchValue({ media: await this.defaultMedia });
            this.toastService.error(this.translateService.translate('calendar.event.error.mediaUpload'));
          },
        });
    }

    openLink(link: string) {

    }

    copyLink(link: string): Promise<void> {
      return clipboardCopy(link);
    }

    private _defaultMedia?: Promise<Media | null>;
    get defaultMedia(): Promise<Media | null> {
      if (!this._defaultMedia) {
        const image = new Image();
        const imageUrl = `${this.document.location.origin}/assets/images/default-media.jpg`;
        image.src = imageUrl;
        this._defaultMedia = new Promise<Media>((resolve, reject) => {
          image.onload = () => {
            const link: ImageLink = {
              src: imageUrl,
              mimeType: 'image/png',
              width: image.width,
              height: image.height,
              size: 0  // unknown
            };
            resolve ({
              id: '0',
              name: 'default',
              type  : 'image',
              links : [ link ],
              cover : link,
              properties: { cover: link },
              rootPath: ''
            } as Media);
          };
          image.onerror = (error) => reject(error);
        }).catch((error) => {
          this.logger.error('Failed to load default media');
          return null;
        });
      }
      return this._defaultMedia;
    }

    isValid(ctrl: AbstractControl): boolean | undefined {
      return ctrl.touched && ctrl.valid ? true : ctrl.touched && ctrl.invalid ? false : undefined;
    }

    get formErrors() {
      const errors: any = {};
      const form = this.form();
      if (form) {
        Object.keys(form.controls).forEach(key => {
          const control = form.get(key);
          errors[key] = {
            value: control?.value,
            valid: control?.valid,
            errors: control?.errors
          };
        });
      }
      return errors;
    }
  }